import {
  DEFAULT_CSS_SELECTOR_ALLOWLIST,
  DEFAULT_DATA_ATTRIBUTE_PREFIX,
} from '@amplitude/plugin-default-event-tracking-advanced-browser';

export const AMPLITUDE_ELEMENT_ATTRIBUTES =
  '[Amplitude] Element Attributes' as const;
export const AMPLITUDE_ELEMENT_CLICKED = '[Amplitude] Element Clicked' as const;
const AMPLITUDE_ELEMENT_CHANGED = '[Amplitude] Element Changed' as const;
export const AMPLITUDE_ELEMENT_SELECTOR =
  '[Amplitude] Element Selector' as const;

type AmplitudeEvent = {
  event_type: string;
  event_properties?: Record<string, unknown>;
};

type ElementHierarchyNode = {
  attrs?: Record<string, string>;
};

function extractPrefixedAttributes(
  attributes: ArrayLike<Attr> | Record<string, string>,
) {
  const entries =
    'length' in attributes
      ? Array.from(attributes as ArrayLike<Attr>).map(({ name, value }) => [
          name,
          value,
        ])
      : Object.entries(attributes);

  return Object.fromEntries(
    entries
      .filter(([name]) => name.startsWith(DEFAULT_DATA_ATTRIBUTE_PREFIX))
      .map(([name, value]) => [
        name.slice(DEFAULT_DATA_ATTRIBUTE_PREFIX.length),
        value,
      ]),
  );
}

export function getElementAnalyticsAttributes(element: Element) {
  const hierarchy: Element[] = [];
  let current: Element | null = element;

  while (current) {
    hierarchy.push(current);
    current = current.parentElement;
  }

  return hierarchy.reverse().reduce<Record<string, string>>(
    (attributes, hierarchyElement) => ({
      ...attributes,
      ...extractPrefixedAttributes(hierarchyElement.attributes),
    }),
    {},
  );
}

export function getHierarchyAnalyticsAttributes(
  hierarchy: ElementHierarchyNode[],
) {
  return hierarchy.toReversed().reduce<Record<string, string>>(
    (attributes, node) => ({
      ...attributes,
      ...extractPrefixedAttributes(node.attrs ?? {}),
    }),
    {},
  );
}

export function getTrackedAnalyticsElement(target: EventTarget | null) {
  if (!(target instanceof Element)) return null;

  return target.closest(DEFAULT_CSS_SELECTOR_ALLOWLIST.join(','));
}

export function getClosestAnalyticsElement(target: EventTarget | null) {
  if (!(target instanceof Element)) return null;

  let current: Element | null = target;
  while (current) {
    if (
      Array.from(current.attributes).some(({ name }) =>
        name.startsWith(DEFAULT_DATA_ATTRIBUTE_PREFIX),
      )
    ) {
      return current;
    }
    current = current.parentElement;
  }

  return null;
}

function resolveElementFromEvent(event: AmplitudeEvent) {
  const hierarchy = event.event_properties?.['[Amplitude] Element Hierarchy'];
  if (Array.isArray(hierarchy)) {
    return getHierarchyAnalyticsAttributes(hierarchy as ElementHierarchyNode[]);
  }

  const selector = event.event_properties?.[AMPLITUDE_ELEMENT_SELECTOR];
  if (typeof selector !== 'string') return null;

  try {
    const element = document.querySelector(selector);
    return element ? getElementAnalyticsAttributes(element) : null;
  } catch {
    return null;
  }
}

export function createAmplitudeElementAttributesPlugin() {
  return {
    name: 'evisort-element-attributes-hierarchy',
    type: 'enrichment' as const,
    async execute(event: AmplitudeEvent) {
      if (
        event.event_type !== AMPLITUDE_ELEMENT_CLICKED &&
        event.event_type !== AMPLITUDE_ELEMENT_CHANGED
      ) {
        return event;
      }

      const attributes = resolveElementFromEvent(event);
      if (!attributes) return event;

      event.event_properties = {
        ...event.event_properties,
        [AMPLITUDE_ELEMENT_ATTRIBUTES]: attributes,
      };

      return event;
    },
  };
}
